import { isStandardErrorResponse } from 'src/api/common';
import Auth from 'src/services/api/auth/Auth';
import { simpleKy } from 'src/services/api/ky/simple-ky';

// Refresh the access token if it will expire within this many seconds, so an
// in-flight request never goes out with an already-expired token.
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 15;

/**
 * Thrown from `beforeRequest` to abort a request we must not send because there
 * is no usable access token. ky rejects the call with this error; since no
 * request goes out there is no Response, so `afterResponse` never runs. Callers
 * (react-query) see a plain error with no `.response`, so it fails cleanly
 * without being mistaken for a 401 and triggering a logout.
 */
export class MissingAuthTokenError extends Error {
    constructor() {
        super('Request aborted: no valid access token available');
        this.name = 'MissingAuthTokenError';
    }
}

/**
 * Extends our simple ky instance with hooks and custom headers.
 *
 * 1. Before each request, it sets the auth headers
 *      Also handles auth token refresh automatically
 * 2. After each response, we redirect the user to the login page if we receive 401 or 422
 */
export const extendedKy = simpleKy.extend({
    hooks: {
        beforeRequest: [
            async (request) => {
                let accessToken = Auth.accessToken;

                if (Auth.isTokenExpiredOrExpiringSoon(accessToken, ACCESS_TOKEN_REFRESH_BUFFER_SECONDS)) {
                    // Both tokens are gone: nothing to refresh, so log out
                    // directly and abort without a wasted round-trip.
                    if (Auth.isRefreshTokenExpired()) {
                        Auth.redirectToLoginPage();
                        throw new MissingAuthTokenError();
                    }

                    // Refresh first, and use the token it returns for this
                    // request. On failure refreshAccessToken returns undefined
                    // (a transient error, or a rejected refresh token that has
                    // already redirected) — abort rather than send an expired
                    // token and get 401'd into a logout.
                    accessToken = await Auth.refreshAccessToken();
                    if (!accessToken) {
                        throw new MissingAuthTokenError();
                    }
                }

                const authHeaders = Auth.getAuthHeaders(accessToken);

                if (!authHeaders) {
                    Auth.redirectToLoginPage();
                    throw new MissingAuthTokenError();
                }

                for (const [key, value] of Object.entries(authHeaders)) {
                    request.headers.set(key, value);
                }
            },
        ],
        afterResponse: [
            async (_request, _options, response) => {
                // 401 is the usual auth failure. 422 is also treated as one
                // because the backend's Flask-JWT-Extended middleware returns
                // 422 (not 401) for an invalid or expired JWT.
                if ([401, 422].includes(response.status)) {
                    Auth.redirectToLoginPage();
                } else if (!response.ok) {
                    const error = await response.json();
                    if (isStandardErrorResponse(error)) {
                        throw error;
                    } else {
                        throw {
                            status: 'error',
                            msg: 'An unknown error occurred',
                        };
                    }
                }
            },
        ],
    },
});
